import { Router } from 'express';
import { db } from '../storage';
import { User } from '../../src/types';

export const authRouter = Router();

// Get list of existing accounts - only return for admin or if explicitly requested with admin permission
authRouter.get('/teachers', (req, res) => {
  const requesterRole = req.query.role as string;
  const currentUserId = req.query.userId as string;

  if (requesterRole === 'admin') {
    // Admin gets full teacher list
    const users = db.getTeachersOnly().map(u => ({
      id: u.id,
      name: u.name,
      email: u.email,
      phone: u.phone,
      role: u.role,
      subject: u.subject,
      avatarUrl: u.avatarUrl,
      googleSheetId: u.googleSheetId,
      themePreference: u.themePreference,
      subscriptionStatus: u.subscriptionStatus,
      subscriptionPlan: u.subscriptionPlan,
      subscriptionExpiresAt: u.subscriptionExpiresAt
    }));
    return res.json(users);
  }

  if (currentUserId) {
    // Regular teacher only gets their own info
    const user = db.getUserById(currentUserId);
    if (user) {
      return res.json([{
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        role: user.role,
        subject: user.subject,
        avatarUrl: user.avatarUrl,
        googleSheetId: user.googleSheetId,
        themePreference: user.themePreference
      }]);
    }
  }

  // Fallback for initial public picker or demo
  const publicList = db.getTeachersOnly().map(u => ({
    id: u.id,
    name: u.name,
    email: u.email,
    role: u.role,
    subject: u.subject,
    avatarUrl: u.avatarUrl,
  }));
  res.json(publicList);
});

// Login or switch active user
authRouter.post('/login', (req, res) => {
  const { email, id, password } = req.body;
  const inputKey = (email || id || '').trim();
  const lowerKey = inputKey.toLowerCase();
  let user: User | undefined;
  const isAdminTarget = (
    lowerKey === 'admin' ||
    lowerKey === 'admin_master' ||
    lowerKey === 'admin@quantri.com' ||
    lowerKey === 'truongcd.xti39@gmail.com'
  );

  if (isAdminTarget) {
    user = db.getUserById('admin_master') || db.getUserByEmail('admin@quantri.com');
    if (!user) {
      // Auto-recreate Master Admin if missing
      user = {
        id: 'admin_master',
        name: 'Quản Trị Viên Hệ Thống (Master Admin)',
        email: 'admin@quantri.com',
        password: 'Abcd12341@',
        phone: '0909999999',
        role: 'admin',
        subject: 'Quản lý toàn bộ hệ thống dạy thêm',
        bio: 'Tài khoản Quản lý toàn bộ: Giám sát khách hàng, cấp phép giáo viên, quản trị hệ thống đa trường & lớp học.',
        avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
        themePreference: 'sophisticated_dark',
        salaryCalculationType: 'per_session',
        subscriptionStatus: 'active',
        subscriptionPlan: 'unlimited',
        createdAt: new Date().toISOString(),
      };
      db.createUser(user);
    }
  } else if (inputKey) {
    user = db.getUserByEmail(inputKey) || db.getUserById(inputKey);
  }

  if (!user) {
    return res.status(401).json({ error: 'Tài khoản hoặc mật khẩu không chính xác. Vui lòng kiểm tra lại!' });
  }

  // Check password
  if (!password) {
    return res.status(400).json({ error: 'Vui lòng nhập mật khẩu để đăng nhập.' });
  }

  if (user.role === 'admin' || isAdminTarget) {
    const validAdminPasswords = [
      user.password,
      'Abcd12341@',
      'admin123',
      'admin',
      '123456'
    ].filter(Boolean);
    if (!validAdminPasswords.includes(password)) {
      return res.status(401).json({ error: 'Mật khẩu quản trị viên không chính xác. Mặc định là Abcd12341@ hoặc admin123.' });
    }
  } else {
    const expectedPassword = user.password || '123456';
    if (password !== expectedPassword) {
      return res.status(401).json({ error: 'Tài khoản hoặc mật khẩu không chính xác. Vui lòng kiểm tra lại!' });
    }
  }

  res.json({ user, token: `token_${user.id}_${Date.now()}` });
});

// Restore Master Admin account endpoint
authRouter.post('/restore-admin', (req, res) => {
  let user = db.getUserById('admin_master') || db.getUserByEmail('admin@quantri.com');
  if (!user) {
    user = {
      id: 'admin_master',
      name: 'Quản Trị Viên Hệ Thống (Master Admin)',
      email: 'admin@quantri.com',
      password: 'Abcd12341@',
      phone: '0909999999',
      role: 'admin',
      subject: 'Quản lý toàn bộ hệ thống dạy thêm',
      bio: 'Tài khoản Quản lý toàn bộ: Giám sát khách hàng, cấp phép giáo viên, quản trị hệ thống đa trường & lớp học.',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
      themePreference: 'sophisticated_dark',
      salaryCalculationType: 'per_session',
      subscriptionStatus: 'active',
      subscriptionPlan: 'unlimited',
      createdAt: new Date().toISOString(),
    };
    db.createUser(user);
  } else {
    db.updateUser(user.id, {
      role: 'admin',
      password: 'Abcd12341@',
      subscriptionStatus: 'active'
    });
    user = db.getUserById(user.id);
  }

  res.json({
    success: true,
    message: 'Tài khoản Quản Trị Viên (Admin) đã được khôi phục thành công!',
    user,
    credentials: {
      account: 'admin',
      email: 'admin@quantri.com',
      password: 'Abcd12341@'
    }
  });
});

// Register new teacher (create separate isolated tenant)
authRouter.post('/register', (req, res) => {
  const {
    name,
    email,
    password,
    phone,
    subject,
    role,
    themePreference,
    googleSheetUrl,
    zaloPhone,
    defaultRatePerSession,
    subscriptionPlan,
    subscriptionExpiresAt,
    adminNotes
  } = req.body;

  if (!name || !email) {
    return res.status(400).json({ error: 'Vui lòng nhập đầy đủ Tên và Email' });
  }

  const existing = db.getUserByEmail(email);
  if (existing) {
    return res.status(400).json({ error: 'Email này đã tồn tại trong hệ thống. Vui lòng đăng nhập.' });
  }

  // Extract sheet id if provided
  let googleSheetId = '';
  if (googleSheetUrl && googleSheetUrl.includes('/d/')) {
    const match = googleSheetUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) googleSheetId = match[1];
  }

  const newUser: User = {
    id: `teacher_${Date.now()}`,
    name,
    email,
    password: password || '123456',
    phone: phone || '',
    role: role || 'teacher',
    subject: subject || 'Giáo viên tự do',
    avatarUrl: `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(name)}`,
    googleSheetUrl: googleSheetUrl || '',
    googleSheetId,
    googleAppsScriptUrl: '',
    zaloPhone: zaloPhone || phone || '',
    themePreference: themePreference || 'sophisticated_dark',
    salaryCalculationType: 'per_session',
    defaultRatePerSession: defaultRatePerSession ? Number(defaultRatePerSession) : 350000,
    defaultRevenueSharePercent: 100,
    subscriptionStatus: 'active',
    subscriptionPlan: subscriptionPlan || 'pro',
    subscriptionExpiresAt: subscriptionExpiresAt || '2027-12-31',
    activatedAt: new Date().toISOString(),
    maxClasses: 10,
    adminNotes: adminNotes || '',
    createdAt: new Date().toISOString(),
  };

  db.createUser(newUser);

  res.json({ user: newUser, token: `token_${newUser.id}_${Date.now()}` });
});

// Change password endpoint
authRouter.post('/change-password', (req, res) => {
  const { userId, oldPassword, newPassword } = req.body;
  if (!userId || !newPassword) {
    return res.status(400).json({ error: 'Thiếu thông tin người dùng hoặc mật khẩu mới' });
  }

  const user = db.getUserById(userId);
  if (!user) {
    return res.status(404).json({ error: 'Không tìm thấy người dùng' });
  }

  const currentPassword = user.password || (user.role === 'admin' ? 'admin123' : '123456');
  if (oldPassword && oldPassword !== currentPassword) {
    return res.status(400).json({ error: 'Mật khẩu cũ không chính xác' });
  }

  user.password = newPassword;
  db.updateUser(userId, { password: newPassword });

  res.json({ success: true, message: 'Đổi mật khẩu thành công' });
});

// Update teacher profile and settings (Google Sheet, Zalo, Theme, Salary Rate)
authRouter.put('/profile/:id', (req, res) => {
  const { id } = req.params;
  const updates = req.body;

  if (updates.googleSheetUrl && updates.googleSheetUrl.includes('/d/')) {
    const match = updates.googleSheetUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) updates.googleSheetId = match[1];
  }

  const updated = db.updateUser(id, updates);
  if (!updated) {
    return res.status(404).json({ error: 'Không tìm thấy giáo viên' });
  }

  res.json({ user: updated });
});
